import React from 'react';

import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating, useMergeRefs } from '@floating-ui/react';
import cx from 'classnames';

import { useToolbarOffset } from 'hooks/useToolbarOffset';

import { useWysiwygContext } from '../Wysiwyg.contex';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Color,
  Delete,
  Italic,
  Link,
  OrderedList,
  Redo,
  UnorderedList,
  Undo,
  Underline,
  Emoji,
} from './Items';
import s from './Toolbar.module.css';
import * as t from './Toolbar.types';

export const Toolbar = () => {
  const { props } = useWysiwygContext();

  switch (props.type) {
    case 'primary':
      return <PrimaryToolbar />;
    case 'secondary':
      return <SecondaryToolbar />;
    case 'tertiary':
      return <TertiaryToolbar />;
  }
};

export const PrimaryToolbar = () => {
  const { props, editor } = useWysiwygContext();
  const tollbarOffset = useToolbarOffset(editor.isFocused);
  const isOpen = editor.isFocused;
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: 'top',
    middleware: [offset({ mainAxis: 6 }), shift()],
    ...props.toolbarFloatingOptions,
  });
  const mergedRef = useMergeRefs([refs.setFloating, editor.refs.toolbarRef]);
  const editorWidth = (() => {
    if (editor.refs.containerRef.current && editor.refs.containerRef.current.getBoundingClientRect) {
      return editor.refs.containerRef.current.getBoundingClientRect().width;
    }
    return 'auto';
  })();

  React.useEffect(() => {
    if (!editor.isFocused) {
      return;
    }

    if (editor.refs.containerRef.current) {
      refs.setReference(editor.refs.containerRef.current);
    }
  }, [editor.isFocused]);

  const itemGroups: t.ItemGroup[] = (() => {
    const groups = [
      [Undo, Redo],
      [Bold, Underline, Italic],
      [UnorderedList, OrderedList],
      [AlignCenter, AlignJustify, AlignLeft, AlignRight],
      [Color, Link, Emoji],
    ];

    if (props.onDelete) {
      groups.push([Delete]);
    }

    return groups;
  })();

  if (props.type === 'secondary' && !editor.isFocused) {
    return null;
  }

  if (!isOpen) {
    return null;
  }

  return (
    <FloatingPortal root={editor.refs.containerRef?.current?.parentElement}>
      <div
        ref={mergedRef}
        style={{ ...floatingStyles, width: editorWidth, top: tollbarOffset.top }}
        className={s.landingWrapper}
      >
        <ToolbarContent itemGroups={itemGroups} />
      </div>
    </FloatingPortal>
  );
};

export const SecondaryToolbar = () => {
  const { props, editor } = useWysiwygContext();

  const itemGroups: t.ItemGroup[] = (() => {
    const groups = [
      [Undo, Redo],
      [Bold, Underline, Italic],
      [UnorderedList, OrderedList],
      [AlignCenter, AlignJustify, AlignLeft, AlignRight],
      [Color, Link],
    ];

    if (props.onDelete) {
      groups.push([Delete]);
    }

    return groups;
  })();

  if (!editor.isFocused) {
    return null;
  }

  return (
    <div
      className={cx(s.staticWrapper, {
        [s.disabled]: props.isDisabled || props.isReadOnly,
      })}
    >
      <ToolbarContent itemGroups={itemGroups} />
    </div>
  );
};

export const TertiaryToolbar = () => {
  const { props, editor } = useWysiwygContext();
  const isOpen = editor.isFocused;
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
    placement: 'top',
  });
  const mergedRef = useMergeRefs([refs.setFloating, editor.refs.toolbarRef]);

  const itemGroups: t.ItemGroup[] = (() => {
    const groups = [
      [Undo, Redo],
      [Bold],
      [Underline],
      [Italic],
      [UnorderedList],
      [OrderedList],
      [AlignCenter],
      [AlignLeft],
      [AlignJustify],
      [AlignRight],
      [Color],
      [Link],
      [Emoji],
    ];

    if (props.onDelete) {
      groups.push([Delete]);
    }

    return groups;
  })();

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div ref={refs.setReference} />
      <FloatingPortal>
        <div ref={mergedRef} style={floatingStyles} className={s.stickyWrapper}>
          <ToolbarContent itemGroups={itemGroups} />
        </div>
      </FloatingPortal>
    </>
  );
};

const ToolbarContent = ({ itemGroups }: { itemGroups: t.ItemGroup[] }) => {
  return (
    <>
      {itemGroups.map((group, groupIndex) => (
        <React.Fragment key={`group-${groupIndex}`}>
          <div className={s.group}>
            {group.map((Item, itemIndex) => (
              <div key={`g-${groupIndex}-i-${itemIndex}`} className={s.item}>
                <Item />
              </div>
            ))}
          </div>

          {groupIndex < itemGroups.length - 1 && <div className={s.separator} />}
        </React.Fragment>
      ))}
    </>
  );
};
